import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { getProductByPriceId } from '../stripe-config'

export interface UserSubscription {
  customer_id: string | null
  stripe_subscription_id: string | null
  subscription_id: string | null
  subscription_status: 'not_started' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused' | null
  price_id: string | null
  current_period_start: number | null
  current_period_end: number | null
  cancel_at_period_end: boolean | null
  payment_method_brand: string | null
  payment_method_last4: string | null
}

export interface UsageData {
  images_generated: number
  bonus_tokens: number
  period_start: string
  period_end: string
}

export interface UserTrialStatus {
  account_type: 'free_trial' | 'paid_subscriber'
  has_used_trial: boolean
  can_generate: boolean
  remaining_trial_images: number
}

export function useSubscription() {
  const { user, isAdmin } = useAuth()
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [usage, setUsage] = useState<UsageData | null>(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const [trialStatus, setTrialStatus] = useState<UserTrialStatus | null>(null)

  useEffect(() => {
    let mounted = true

    if (!user) {
      console.log('[useSubscription] No user, clearing subscription')
      setSubscription(null)
      setUsage(null)
      setTrialStatus(null)
      setLoading(false)
      setUsageLoading(false)
      return
    }

    const fetchSubscriptionAndUsage = async () => {
      try {
        console.log('[useSubscription] Fetching subscription and usage in parallel for user:', user.id)

        // OPTIMIZATION: Fetch subscription, usage, and trial status in parallel
        const [subscriptionResult, usageResult, trialResult] = await Promise.all([
          supabase
            .from('stripe_user_subscriptions')
            .select('*')
            .limit(1)
            .maybeSingle(),
          supabase.rpc('get_current_usage', { p_user_id: user.id }),
          supabase.rpc('get_user_trial_status', { p_user_id: user.id })
        ])

        if (!mounted) return

        // Handle subscription result
        if (subscriptionResult.error) {
          console.error('[useSubscription] Error fetching subscription:', subscriptionResult.error)
          setSubscription(null)
        } else {
          console.log('[useSubscription] Subscription data received:', subscriptionResult.data)
          console.log('[useSubscription] Subscription status:', subscriptionResult.data?.subscription_status)
          console.log('[useSubscription] Price ID:', subscriptionResult.data?.price_id)
          setSubscription(subscriptionResult.data)
        }

        // Handle usage result
        if (usageResult.error) {
          console.error('[useSubscription] Error fetching usage:', usageResult.error)
          setUsage(null)
        } else if (usageResult.data && usageResult.data.length > 0) {
          console.log('[useSubscription] Usage data received:', usageResult.data[0])
          setUsage(usageResult.data[0])
        } else {
          setUsage({ images_generated: 0, bonus_tokens: 0, period_start: '', period_end: '' })
        }

        // Handle trial status result
        if (trialResult.error) {
          console.error('[useSubscription] Error fetching trial status:', trialResult.error)
          setTrialStatus(null)
        } else if (trialResult.data && trialResult.data.length > 0) {
          console.log('[useSubscription] Trial status received:', trialResult.data[0])
          setTrialStatus(trialResult.data[0])
        } else {
          setTrialStatus({ account_type: 'paid_subscriber', has_used_trial: false, can_generate: true, remaining_trial_images: 0 })
        }

      } catch (error) {
        console.error('[useSubscription] Exception while fetching:', error)
        if (mounted) {
          setSubscription(null)
          setUsage(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
          setUsageLoading(false)
        }
      }
    }

    fetchSubscriptionAndUsage()

    return () => {
      mounted = false
    }
  }, [user])

  const getActivePlan = () => {
    if (!subscription || !subscription.price_id) return null
    
    const product = getProductByPriceId(subscription.price_id)
    return product ? product.name : null
  }

  const isActive = () => {
    // Free trial users who haven't used their trial are considered "active"
    if (trialStatus?.account_type === 'free_trial' && !trialStatus?.has_used_trial) {
      console.log('[useSubscription.isActive] Free trial user with unused trial')
      return true
    }

    const active = subscription?.subscription_status === 'active' || subscription?.subscription_status === 'trialing'
    console.log('[useSubscription.isActive] Checking subscription:', {
      hasSubscription: !!subscription,
      status: subscription?.subscription_status,
      isActive: active
    })
    return active
  }

  const fetchUsage = useCallback(async () => {
    if (!user) {
      setUsage(null)
      setUsageLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.rpc('get_current_usage', {
        p_user_id: user.id
      })

      if (error) throw error

      if (data && data.length > 0) {
        setUsage(data[0])
      } else {
        setUsage({ images_generated: 0, bonus_tokens: 0, period_start: '', period_end: '' })
      }
    } catch (error) {
      console.error('Error fetching usage:', error)
      setUsage(null)
    } finally {
      setUsageLoading(false)
    }
  }, [user])

  const incrementUsage = useCallback(async (count: number = 1) => {
    if (!user) return

    try {
      const { data, error } = await supabase.rpc('increment_usage', {
        p_user_id: user.id,
        p_count: count
      })

      if (error) throw error

      await fetchUsage()
      return data
    } catch (error) {
      console.error('Error incrementing usage:', error)
      throw error
    }
  }, [user, fetchUsage])

  const hasReachedLimit = useCallback(() => {
    if (isAdmin) {
      console.log('[useSubscription.hasReachedLimit] Admin user, unlimited access')
      return false
    }

    // Special handling for free trial users
    if (trialStatus?.account_type === 'free_trial') {
      if (trialStatus.has_used_trial) {
        console.log('[useSubscription.hasReachedLimit] Free trial already used')
        return true
      }
      console.log('[useSubscription.hasReachedLimit] Free trial available, not reached limit')
      return false
    }

    // Check if user has bonus tokens available, even without active subscription
    const bonusTokens = usage?.bonus_tokens || 0
    const imagesGenerated = usage?.images_generated || 0

    // If subscription is not active, check bonus tokens only
    if (!subscription || !isActive()) {
      console.log('[useSubscription.hasReachedLimit] No active subscription, checking bonus tokens only')
      console.log('[useSubscription.hasReachedLimit] Bonus tokens:', bonusTokens, 'Images generated:', imagesGenerated)
      // Allow generation if they have bonus tokens remaining
      if (bonusTokens > 0 && imagesGenerated < bonusTokens) {
        console.log('[useSubscription.hasReachedLimit] Bonus tokens available, allowing generation')
        return false
      }
      console.log('[useSubscription.hasReachedLimit] No bonus tokens available')
      return true
    }

    const product = subscription.price_id ? getProductByPriceId(subscription.price_id) : null
    if (!product) {
      console.log('[useSubscription.hasReachedLimit] No product found for price_id:', subscription.price_id)
      // Even without product, check bonus tokens
      if (bonusTokens > 0 && imagesGenerated < bonusTokens) {
        return false
      }
      return true
    }

    if (product.imageLimit === null) {
      console.log('[useSubscription.hasReachedLimit] Unlimited plan, no limit')
      return false
    }

    const totalAvailable = product.imageLimit + bonusTokens
    const hasReached = imagesGenerated >= totalAvailable
    console.log('[useSubscription.hasReachedLimit] Limit check:', {
      imageLimit: product.imageLimit,
      bonusTokens,
      totalAvailable,
      imagesGenerated,
      hasReached
    })
    return hasReached
  }, [isAdmin, subscription, usage, trialStatus])

  const getRemainingImages = useCallback(() => {
    if (isAdmin) return Infinity

    const bonusTokens = usage?.bonus_tokens || 0
    const imagesGenerated = usage?.images_generated || 0

    // If subscription is not active, return bonus tokens only
    if (!subscription || !isActive()) {
      return Math.max(0, bonusTokens - imagesGenerated)
    }

    const product = subscription.price_id ? getProductByPriceId(subscription.price_id) : null
    if (!product) {
      // No product, but might have bonus tokens
      return Math.max(0, bonusTokens - imagesGenerated)
    }

    if (product.imageLimit === null) return Infinity

    const totalAvailable = product.imageLimit + bonusTokens
    return Math.max(0, totalAvailable - imagesGenerated)
  }, [isAdmin, subscription, usage])

  const getBonusTokens = useCallback(() => {
    return usage?.bonus_tokens || 0
  }, [usage])

  const getSubscriptionLimit = useCallback(() => {
    if (!subscription || !isActive()) return 0

    const product = subscription.price_id ? getProductByPriceId(subscription.price_id) : null
    if (!product) return 0

    return product.imageLimit
  }, [subscription])

  const isFreeTrial = useCallback(() => {
    return trialStatus?.account_type === 'free_trial'
  }, [trialStatus])

  const getRemainingTrialImages = useCallback(() => {
    if (trialStatus?.account_type === 'free_trial' && !trialStatus.has_used_trial) {
      return 4
    }
    return 0
  }, [trialStatus])

  const markTrialAsUsed = useCallback(async () => {
    if (!user || trialStatus?.account_type !== 'free_trial') return false

    try {
      const { data, error } = await supabase.rpc('mark_trial_as_used', {
        p_user_id: user.id
      })

      if (error) throw error

      // Refresh trial status
      const { data: newTrialStatus, error: fetchError } = await supabase.rpc('get_user_trial_status', {
        p_user_id: user.id
      })

      if (!fetchError && newTrialStatus && newTrialStatus.length > 0) {
        setTrialStatus(newTrialStatus[0])
      }

      return data
    } catch (error) {
      console.error('Error marking trial as used:', error)
      return false
    }
  }, [user, trialStatus])

  const hasActiveSubscription = isAdmin || subscription?.subscription_status === 'active' || subscription?.subscription_status === 'trialing';
  const currentProduct = subscription?.price_id ? getProductByPriceId(subscription.price_id) : null;
  const imageLimit = isAdmin ? null : currentProduct?.imageLimit;
  const imagesUsed = usage?.images_generated || 0;
  const bonusTokens = usage?.bonus_tokens || 0;
  const totalAvailable = isAdmin ? null : (imageLimit !== null && imageLimit !== undefined ? imageLimit + bonusTokens : null);
  const canGenerateImages = isAdmin || (hasActiveSubscription && (imageLimit === null || (totalAvailable !== null && imagesUsed < totalAvailable)));

  console.log('[useSubscription] Computed values:', {
    hasActiveSubscription,
    currentProduct: currentProduct?.name,
    imageLimit,
    imagesUsed,
    bonusTokens,
    totalAvailable,
    canGenerateImages
  });

  return {
    subscription,
    loading,
    hasActiveSubscription,
    currentProduct,
    imageLimit,
    imagesUsed,
    bonusTokens,
    totalAvailable,
    canGenerateImages,
    usage,
    usageLoading,
    trialStatus,
    getActivePlan,
    isActive,
    incrementUsage,
    hasReachedLimit,
    getRemainingImages,
    getBonusTokens,
    getSubscriptionLimit,
    refetchUsage: fetchUsage,
    isFreeTrial,
    getRemainingTrialImages,
    markTrialAsUsed
  }
}